/**
 * REA API Error Formatter Utility
 * 
 * Formats REA API error responses into user-friendly messages
 */

/**
 * Formats REA API errors into a readable string
 * @param {Object|Array|string} errorData - Error data from REA API response
 * @returns {string} - Formatted error message
 */
export const formatREAError = (errorData) => {
    if (!errorData) {
        return "An unknown error occurred while processing your property.";
    }

    // If it's already a string, return it
    if (typeof errorData === 'string') {
        return errorData;
    }

    // If it's an array, join the errors
    if (Array.isArray(errorData)) {
        if (errorData.length === 0) {
            return "An error occurred while processing your property.";
        }
        if (errorData.length === 1) {
            return formatSingleError(errorData[0]);
        }
        // Multiple errors - format as numbered list
        return errorData
            .map((err, idx) => `${idx + 1}. ${formatSingleError(err)}`)
            .join('\n');
    }

    // If it's an object, extract error messages
    if (typeof errorData === 'object') {
        const errors = [];
        
        // Check common error fields
        if (errorData.message) {
            errors.push(errorData.message);
        }
        
        if (errorData.errors) {
            if (Array.isArray(errorData.errors)) {
                errors.push(...errorData.errors.map(e => formatSingleError(e)));
            } else {
                errors.push(formatSingleError(errorData.errors));
            }
        }
        
        if (errorData.detail) {
            errors.push(errorData.detail);
        }
        
        // Check for field-specific errors (including REA API structure)
        Object.keys(errorData).forEach(key => {
            if (!['message', 'errors', 'detail', 'rea_errors', 'completed', 'upload_id'].includes(key)) {
                const value = errorData[key];
                if (value) {
                    if (Array.isArray(value)) {
                        value.forEach(v => {
                            // Format each error in the array
                            const formattedError = formatSingleError(v);
                            // Only add if it's not already a formatted field error
                            if (!formattedError.startsWith(formatFieldName(key) + ':')) {
                                const fieldName = formatFieldName(key);
                                errors.push(`${fieldName}: ${formattedError}`);
                            } else {
                                errors.push(formattedError);
                            }
                        });
                    } else if (typeof value === 'object' && value !== null) {
                        // Handle REA API error structure: {type: 'listing', field: 'suburb', messages: [...]}
                        if (value.messages && Array.isArray(value.messages)) {
                            // This is a REA API error structure - formatSingleError will handle it
                            errors.push(formatSingleError(value));
                        } else {
                            // Regular object error
                            const formattedError = formatSingleError(value);
                            if (!formattedError.startsWith(formatFieldName(key) + ':')) {
                                const fieldName = formatFieldName(key);
                                errors.push(`${fieldName}: ${formattedError}`);
                            } else {
                                errors.push(formattedError);
                            }
                        }
                    } else {
                        // Simple string or primitive value
                        const fieldName = formatFieldName(key);
                        errors.push(`${fieldName}: ${formatSingleError(value)}`);
                    }
                }
            }
        });
        
        if (errors.length > 0) {
            return errors.length === 1 ? errors[0] : errors.join('\n');
        }
    }

    return "An error occurred while processing your property.";
};

/**
 * Formats a single error item (could be string or object)
 * @param {string|Object} error - Single error item
 * @returns {string} - Formatted error message
 */
const formatSingleError = (error) => {
    let errorStr = '';
    
    if (typeof error === 'string') {
        errorStr = error;
    } else if (typeof error === 'object' && error !== null) {
        // Handle REA API error structure: {type: 'listing', field: 'suburb', messages: [...], helpCentreUrl: '...'}
        if (error.messages && Array.isArray(error.messages) && error.messages.length > 0) {
            const fieldName = error.field ? formatFieldName(error.field) : 'Field';
            const messages = error.messages.map(msg => {
                // Extract the actual error message, removing quotes around values
                let cleanMsg = String(msg);
                // Remove quotes around suburb/address values for better readability
                cleanMsg = cleanMsg.replace(/"([^"]+)"/g, (match, value) => {
                    // Keep the value but make it clearer
                    return `"${value}"`;
                });
                return cleanMsg;
            });
            
            // Format as user-friendly message
            if (messages.length === 1) {
                errorStr = `${fieldName}: ${messages[0]}`;
            } else {
                errorStr = `${fieldName}:\n${messages.map((msg, idx) => `  ${idx + 1}. ${msg}`).join('\n')}`;
            }
        }
        // Handle standard error object with message property
        else if (error.message) {
            const field = error.field ? `${formatFieldName(error.field)}: ` : '';
            errorStr = `${field}${error.message}`;
        } 
        // Handle error object with just a string value
        else if (typeof error === 'object' && Object.keys(error).length === 1 && error[Object.keys(error)[0]]) {
            const key = Object.keys(error)[0];
            const value = error[key];
            if (typeof value === 'string') {
                errorStr = `${formatFieldName(key)}: ${value}`;
            } else {
                // Recursively format nested error objects
                errorStr = formatSingleError(value);
            }
        }
        // Fallback: try to stringify if it's an object
        else {
            // Check if it's a simple key-value error object
            const keys = Object.keys(error);
            if (keys.length > 0 && !keys.includes('type') && !keys.includes('field') && !keys.includes('messages')) {
                // It might be a simple field error like {suburb: "error message"}
                const firstKey = keys[0];
                const firstValue = error[firstKey];
                if (typeof firstValue === 'string') {
                    errorStr = `${formatFieldName(firstKey)}: ${firstValue}`;
                } else {
                    errorStr = JSON.stringify(error);
                }
            } else {
                errorStr = JSON.stringify(error);
            }
        }
    } else {
        errorStr = String(error);
    }
    
    // Detect and format 404 "Upload not found" errors
    if (errorStr.includes('404') || errorStr.includes('Not Found') || errorStr.includes('Upload not found')) {
        // Check if this is specifically about upload not found
        if (errorStr.includes('Upload not found') || errorStr.includes('upload not found')) {
            return `Upload Not Found: The property upload could not be found.\n\n` +
                   `This usually means the initial upload failed validation. Common causes:\n` +
                   `• Invalid postcode format (must be exactly 4 digits)\n` +
                   `• Invalid suburb name or suburb doesn't match postcode\n` +
                   `• Price below minimum ($2,900)\n` +
                   `• Missing required fields\n` +
                   `• Invalid property data format\n\n` +
                   `Please check:\n` +
                   `• Postcode: Must be exactly 4 digits (e.g., "3219" not "321" or "32190")\n` +
                   `• Suburb: Must match the postcode and be spelled correctly\n` +
                   `• Price: Must be at least $2,900\n` +
                   `• All required fields are filled correctly\n\n` +
                   `Fix any errors and try submitting again.`;
        }
        // Generic 404 error
        return `Resource Not Found: The requested resource could not be found.\n\n` +
               `Please try submitting the property again. If the problem persists, check all form fields for errors.`;
    }
    
    // Detect and format encoding errors
    if (errorStr.includes("latin-1 codec can't encode") || 
        errorStr.includes("codec can't encode character") ||
        errorStr.includes("is not valid Latin-1")) {
        return "Your property description contains special characters (like smart quotes or em dashes) that cannot be processed. Please replace them with regular characters:\n" +
               "• Replace ' (smart quote) with ' (regular apostrophe)\n" +
               "• Replace \" (smart quote) with \" (regular quote)\n" +
               "• Replace — (em dash) with - (hyphen)\n" +
               "• Replace – (en dash) with - (hyphen)\n\n" +
               "Please edit your description and try again.";
    }
    
    // Detect and format invalid postcode errors FIRST (before suburb errors)
    // REA API sometimes reports postcode errors as suburb errors, so check for postcode patterns
    const postcodePattern = /\b(\d{1,3}|\d{5,})\b/; // Matches postcodes that are not 4 digits
    const postcodeInError = errorStr.match(/(?:postcode|post code|postal code)[^"]*"?([^"]*"?\d{1,3}|\d{5,}[^"]*")/i) || 
                           errorStr.match(/"([^"]*,\s*(?:Vic|NSW|QLD|SA|WA|TAS|NT|ACT)\s+\d{1,3}|\d{5,}[^"]*)"/i);
    
    if (postcodeInError || (errorStr.includes('postcode') || errorStr.includes('Postcode') || errorStr.includes('post code'))) {
        if (errorStr.includes('invalid') || errorStr.includes('not valid') || errorStr.includes('does not match') || postcodeInError) {
            // Extract postcode from error if present
            const postcodeMatch = errorStr.match(/\b(\d{1,3}|\d{5,})\b/);
            const invalidPostcode = postcodeMatch ? postcodeMatch[1] : null;
            
            let message = `Invalid Postcode: The postcode you entered is not valid.\n\n`;
            
            if (invalidPostcode) {
                if (invalidPostcode.length < 4) {
                    message += `Your postcode "${invalidPostcode}" is too short.\n`;
                } else if (invalidPostcode.length > 4) {
                    message += `Your postcode "${invalidPostcode}" is too long.\n`;
                }
            }
            
            message += `Please check:\n` +
                       `• Postcode must be exactly 4 digits (e.g., "3219" not "321" or "32190")\n` +
                       `• Postcode must match the suburb and state\n` +
                       `• Ensure there are no spaces or special characters\n` +
                       `• Remove postcode from the suburb field (suburb should only contain the suburb name)\n\n` +
                       `Please correct the postcode and try again.`;
            
            return message;
        }
    }
    
    // Detect and format invalid suburb/address errors
    // Check if error contains suburb name with postcode/state (which might actually be a postcode error)
    if (errorStr.includes('is invalid') && (errorStr.includes('suburb') || errorStr.includes('Suburb'))) {
        // Extract the invalid value from the error message
        const match = errorStr.match(/"([^"]+)"/);
        if (match) {
            const invalidValue = match[1];
            
            // Check if the error value contains a postcode pattern (not 4 digits)
            const postcodeInValue = invalidValue.match(/\b(\d{1,3}|\d{5,})\b/);
            
            // If it contains an invalid postcode format, prioritize postcode error
            if (postcodeInValue && (postcodeInValue[1].length !== 4)) {
                const invalidPostcode = postcodeInValue[1];
                return `Invalid Postcode: The postcode "${invalidPostcode}" is not valid.\n\n` +
                       `Please check:\n` +
                       `• Postcode must be exactly 4 digits (e.g., "3219" not "${invalidPostcode}")\n` +
                       `• Postcode must match the suburb and state\n` +
                       `• Ensure there are no spaces or special characters\n` +
                       `• Remove postcode and state from the suburb field\n` +
                       `• Suburb field should only contain the suburb name (e.g., "Geelong" not "Geelong, Vic 321")\n\n` +
                       `Please correct the postcode and try again.`;
            }
            
            // Otherwise, treat as suburb error
            return `Invalid Suburb: The suburb "${invalidValue}" is not recognized.\n\n` +
                   `Please check:\n` +
                   `• Ensure the suburb name is spelled correctly (e.g., "Geelong" not "Glong")\n` +
                   `• Remove any extra information like postcode or state from the suburb field\n` +
                   `• Use only the suburb name (e.g., "East Geelong" not "East Geelong, VIC 3219")\n` +
                   `• Postcode and state should be in their respective fields\n\n` +
                   `Please correct the suburb name and try again.`;
        }
    }
    
    // Detect and format price errors
    if (errorStr.includes('price') || errorStr.includes('Price')) {
        if (errorStr.includes('minimum') || errorStr.includes('too low') || errorStr.includes('less than')) {
            return `Invalid Price: The price you entered is too low.\n\n` +
                   `Please check:\n` +
                   `• Minimum price is $2,900\n` +
                   `• Ensure you're entering a valid number\n` +
                   `• Remove any currency symbols or commas (e.g., enter "3500" not "$3,500")\n\n` +
                   `Please correct the price and try again.`;
        }
        if (errorStr.includes('maximum') || errorStr.includes('too high')) {
            return `Invalid Price: The price you entered exceeds the maximum allowed.\n\n` +
                   `Please check the price value and try again.`;
        }
    }
    
    // Detect and format bedroom/bathroom/garage errors
    if (errorStr.includes('bedroom') || errorStr.includes('bathroom') || errorStr.includes('garage') || 
        errorStr.includes('parking') || errorStr.includes('car space')) {
        if (errorStr.includes('maximum') || errorStr.includes('exceed') || errorStr.includes('too many')) {
            return `Invalid Property Features: The number you entered exceeds the maximum allowed.\n\n` +
                   `Please check:\n` +
                   `• Maximum bedrooms: 50\n` +
                   `• Maximum bathrooms: 50\n` +
                   `• Maximum parking spaces: 50\n` +
                   `• Ensure you're entering whole numbers only\n\n` +
                   `Please correct the values and try again.`;
        }
        if (errorStr.includes('minimum') || errorStr.includes('negative') || errorStr.includes('less than')) {
            return `Invalid Property Features: The number you entered is invalid.\n\n` +
                   `Please check:\n` +
                   `• Values must be 0 or greater\n` +
                   `• Ensure you're entering whole numbers only\n\n` +
                   `Please correct the values and try again.`;
        }
    }
    
    // Detect and format image/photo errors
    if (errorStr.includes('image') || errorStr.includes('photo') || errorStr.includes('picture') || 
        errorStr.includes('Image') || errorStr.includes('Photo')) {
        if (errorStr.includes('required') || errorStr.includes('at least') || errorStr.includes('minimum')) {
            return `Images Required: At least one property image is required.\n\n` +
                   `Please check:\n` +
                   `• Add at least 1 image of the property\n` +
                   `• Maximum 5 images allowed\n` +
                   `• Ensure images are in valid formats (JPG, PNG)\n` +
                   `• Check that image files are not corrupted\n\n` +
                   `Please add images and try again.`;
        }
        if (errorStr.includes('maximum') || errorStr.includes('too many') || errorStr.includes('exceed')) {
            return `Too Many Images: You have exceeded the maximum number of images allowed.\n\n` +
                   `Please check:\n` +
                   `• Maximum 5 images allowed\n` +
                   `• Remove extra images and keep only the best 5\n\n` +
                   `Please reduce the number of images and try again.`;
        }
        if (errorStr.includes('format') || errorStr.includes('invalid') || errorStr.includes('not supported')) {
            return `Invalid Image Format: One or more images are in an unsupported format.\n\n` +
                   `Please check:\n` +
                   `• Supported formats: JPG, JPEG, PNG\n` +
                   `• Ensure images are not corrupted\n` +
                   `• Try converting images to JPG format\n\n` +
                   `Please update your images and try again.`;
        }
    }
    
    // Detect and format required field errors
    if (errorStr.includes('required') || errorStr.includes('must be provided') || errorStr.includes('cannot be empty')) {
        const fieldMatch = errorStr.match(/(street|suburb|state|postcode|category|headline|description|address|price)/i);
        if (fieldMatch) {
            const field = fieldMatch[1].toLowerCase();
            const fieldNames = {
                'street': 'Street Address',
                'address': 'Address',
                'suburb': 'Suburb',
                'state': 'State',
                'postcode': 'Postcode',
                'category': 'Property Category',
                'headline': 'Headline/Title',
                'description': 'Description',
                'price': 'Price'
            };
            const fieldName = fieldNames[field] || field;
            return `Required Field Missing: ${fieldName} is required.\n\n` +
                   `Please fill in the ${fieldName.toLowerCase()} field and try again.`;
        }
    }
    
    // Detect and format field length errors
    if (errorStr.includes('too long') || errorStr.includes('exceeds') || errorStr.includes('maximum length') || 
        errorStr.includes('max length') || errorStr.includes('characters')) {
        const fieldMatch = errorStr.match(/(headline|street|suburb|state|postcode|description|category)/i);
        if (fieldMatch) {
            const field = fieldMatch[1].toLowerCase();
            const maxLengths = {
                'headline': 255,
                'street': 100,
                'suburb': 100,
                'state': 30,
                'postcode': 30,
                'category': 50
            };
            const maxLength = maxLengths[field];
            if (maxLength) {
                return `Field Too Long: The ${field} field exceeds the maximum length.\n\n` +
                       `Please check:\n` +
                       `• Maximum length for ${field}: ${maxLength} characters\n` +
                       `• Shorten the text and try again\n\n` +
                       `Please reduce the length and try again.`;
            }
        }
    }
    
    return errorStr;
};

/**
 * Formats field names to be more readable
 * @param {string} fieldName - Field name (e.g., "street_number")
 * @returns {string} - Formatted field name (e.g., "Street Number")
 */
const formatFieldName = (fieldName) => {
    return fieldName
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Extracts errors from API response
 * @param {Object} response - API response object
 * @returns {Array<string>} - Array of error messages
 */
export const extractErrorsFromResponse = (response) => {
    const errors = [];
    
    if (!response) {
        return ["No response received from server"];
    }
    
    const data = response.data || response;
    
    // Check for errors array
    if (data.errors) {
        if (Array.isArray(data.errors)) {
            errors.push(...data.errors.map(e => formatSingleError(e)));
        } else {
            errors.push(formatSingleError(data.errors));
        }
    }
    
    // Check for REA-specific errors
    if (data.rea_errors) {
        if (Array.isArray(data.rea_errors)) {
            errors.push(...data.rea_errors.map(e => formatSingleError(e)));
        } else {
            errors.push(formatSingleError(data.rea_errors));
        }
    }
    
    // Check for detail message
    if (data.detail && !data.detail.includes("successfully")) {
        errors.push(data.detail);
    }
    
    // Check for field-specific errors (including REA API structure)
    if (typeof data === 'object') {
        Object.keys(data).forEach(key => {
            if (!['errors', 'rea_errors', 'detail', 'completed', 'upload_id'].includes(key)) {
                const value = data[key];
                if (value) {
                    if (typeof value === 'object' && value !== null) {
                        // Handle REA API error structure: {type: 'listing', field: 'suburb', messages: [...]}
                        if (value.messages && Array.isArray(value.messages)) {
                            errors.push(formatSingleError(value));
                        } else {
                            // Recursively format nested error objects
                            const formattedError = formatSingleError(value);
                            if (!formattedError.startsWith(formatFieldName(key) + ':')) {
                                const fieldName = formatFieldName(key);
                                errors.push(`${fieldName}: ${formattedError}`);
                            } else {
                                errors.push(formattedError);
                            }
                        }
                    } else {
                        const fieldName = formatFieldName(key);
                        errors.push(`${fieldName}: ${formatSingleError(value)}`);
                    }
                }
            }
        });
    }
    
    return errors.length > 0 ? errors : ["An unknown error occurred"];
};

/**
 * Formats errors for toast notification display
 * @param {Object|Array|string} errorData - Error data
 * @param {number} maxLength - Maximum length before truncation (default: 500)
 * @returns {string} - Formatted error message for toast
 */
export const formatErrorForToast = (errorData, maxLength = 500) => {
    const formatted = formatREAError(errorData);
    
    if (formatted.length > maxLength) {
        return formatted.substring(0, maxLength) + '...';
    }
    
    return formatted;
};
